import { useCallback, useMemo, useState } from 'react';
import { filterVisibleTeamOptions } from '../shared/utils/teamHierarchy';

/**
 * Manages the expand/collapse state for a hierarchical team dropdown and
 * returns the list of currently visible options.
 *
 * @param allTeams The full flat list (produced by flattenTeamsWithMetadata).
 *
 * @example
 * ```tsx
 * const allTeams = useMemo(() => flattenTeamsWithMetadata(teamsData?.dto), [teamsData]);
 * const { expandedTeamPaths, toggleTeamExpand, visibleTeams } = useTeamHierarchy(allTeams);
 * ```
 */
export function useTeamHierarchy<
  T extends { depth?: number; teamPath?: string }
>(
  allTeams: T[]
): {
  expandedTeamPaths: Set<string>;
  toggleTeamExpand: (teamPath: string) => void;
  visibleTeams: T[];
} {
  const [expandedTeamPaths, setExpandedTeamPaths] = useState<Set<string>>(
    new Set()
  );

  const toggleTeamExpand = useCallback((teamPath: string) => {
    setExpandedTeamPaths((prev) => {
      const next = new Set(prev);
      if (next.has(teamPath)) next.delete(teamPath);
      else next.add(teamPath);
      return next;
    });
  }, []);

  const visibleTeams = useMemo(
    () => filterVisibleTeamOptions(allTeams, expandedTeamPaths),
    [allTeams, expandedTeamPaths]
  );

  return { expandedTeamPaths, toggleTeamExpand, visibleTeams };
}
